import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import ListCell from 'components/list/list-cell';
import NewListRow from 'components/new-list-row';
import dayjs from 'dayjs';
import { Circle, Clock, Minus, TriangleAlert } from 'lucide-react';
import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import ManualRecommendationSkipDialog from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/recommendations/dialogs/manual-skip-dialog';
import StandardRecommendationRowBody from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/recommendations/standard/standard-recommendation-row-body';
import { Recommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/recommendation-assortment.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { OrderType, SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';

type Props = {
  recommendation: Recommendation;
};

const cellAlignment = {
  width: '14%',
  maxWidth: '14%',
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders.list';

const AutoConfirmDays: React.FC<{ day: number }> = ({ day }) => (
  <Typography>
    <FormattedMessage id={`${PAGE_NAME_ID}.${day === 0 ? 'today' : day === 1 ? 'day' : 'days'}`} values={{ days: day }} />
  </Typography>
);

const StandardRecommendationRow: React.FC<Props> = ({ recommendation }) => {
  const theme = useTheme();
  const { createOrderFeedEvent } = useGoogleTagManager();

  const totalQuantity = useMemo(() => {
    return recommendation.products.reduce((prev, curr) => prev + curr.quantity, 0);
  }, [recommendation]);

  const isSkipped = recommendation.autoSkipped || recommendation.manuallySkipped;

  const iconColor = useMemo(
    () => (isSkipped ? theme.palette.darkBlue.o25 : theme.palette.orange.o75),
    [recommendation]
  );


  return (
    <NewListRow
      colSpan={7}
      sx={{ background: theme.palette.common.white }}
      body={<StandardRecommendationRowBody recommendation={recommendation} />}
      bodyOnClick={() => {
        createOrderFeedEvent('dropdown', {
          supplyPlanType: SupplyPlanType.OFD2,
          orderType: recommendation.autoSkipped ? OrderType.SKIPPED : OrderType.FORECAST,
        });
      }}>
      <ListCell sx={cellAlignment}>
        <Box display={'flex'} gap={4} alignItems={'center'}>
          {isSkipped ? (
            <IconButton
              sx={{ backgroundColor: theme.palette.darkBlue.o25, cursor: 'unset' }}
              disableRipple
              disableTouchRipple
              size="small">
              <Minus color={theme.palette.common.black} size={17} />
            </IconButton>
          ) : (
            <IconButton
              sx={{ backgroundColor: theme.palette.orange.light, cursor: 'unset' }}
              disableRipple
              disableTouchRipple
              size="small">
              <Clock color={theme.palette.common.black} size={17} />
            </IconButton>
          )}
          <Box>
            {isSkipped ? (
              <FormattedMessage id={`${PAGE_NAME_ID}.skipped`} values={{ type: recommendation.manuallySkipped ? 'Manual' : 'Auto' }} />
            ) : (
              <FormattedMessage id={`${PAGE_NAME_ID}.forecast`} />
            )}
          </Box>
        </Box>
      </ListCell>
      <ListCell sx={cellAlignment}>
        {!isSkipped ? <AutoConfirmDays day={recommendation.autoConfirmInDays} /> : '-'}
      </ListCell>
      <ListCell sx={cellAlignment}>{dayjs(recommendation.dispatch).format('DD MMM YYYY')}</ListCell>
      <ListCell sx={cellAlignment}>
        {recommendation.delivery !== null ? dayjs(recommendation.delivery).format('DD MMM YYYY') : '-'}
      </ListCell>
      <ListCell align="right" sx={cellAlignment}>
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={2}
          sx={{
            backgroundColor: theme.palette.orange.light,
            borderRadius: 50,
            height: 36,
          }}>
          {recommendation.productsOutsideCustomerAssortment.length > 0 ? (
            <Tooltip title={<FormattedMessage id={`${PAGE_NAME_ID}.body.productNotAvailable`} />}>
              <TriangleAlert fill={theme.palette.warning.main} strokeWidth={1} stroke={theme.palette.darkBlue.main} />
            </Tooltip>
          ) : (
            <Circle size={8} fill={iconColor} color={iconColor} />
          )}
          <FormattedNumber
            value={totalQuantity}
            style={`unit`}
            unit={'kilogram'}
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        </Box>
      </ListCell>
      <ListCell sx={{ width: '100%' }} align="right">
        {recommendation.manuallySkipped ? (
          <ManualRecommendationSkipDialog recommendation={recommendation} />
        ) : null}
      </ListCell>
    </NewListRow>
  );
};

export default React.memo(StandardRecommendationRow);
